import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, dateFmt } from "@/lib/api";
import { StatusBadge, UrgencyBadge } from "@/components/StatusBadge";
import { Activity, Clock, Receipt, TrendingUp, ArrowRight, PlusCircle, AlertTriangle, Timer, CheckCircle2 } from "lucide-react";
import DownloadManualButton from "@/components/DownloadManualButton";

const Kpi = ({ icon: Icon, label, value, sub, tone = "default", testId }) => {
    const tones = {
        default: "border-slate-200",
        accent: "border-l-4 border-l-red-600 border-slate-200",
        warning: "border-l-4 border-l-amber-500 border-slate-200",
        positive: "border-l-4 border-l-emerald-500 border-slate-200",
    };
    return (
        <div className={`fm-card p-4 ${tones[tone]}`} data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</span>
                <Icon className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
            </div>
            <div className="font-mono font-bold text-2xl tabular-nums text-slate-900 leading-tight">{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
        </div>
    );
};

export default function PortalHome() {
    const [data, setData] = useState(null);
    const [sla, setSla] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/portal/dashboard");
                if (r?.data) setData(r.data);
            } catch (e) { console.warn(e); }
        })();
        (async () => {
            try {
                const r = await api.get("/portal/sla?days=30");
                if (r?.data) setSla(r.data);
            } catch (e) { console.warn(e); }
        })();
    }, []);

    if (!data) return <div className="text-sm text-slate-500 font-mono">Cargando...</div>;

    return (
        <div className="space-y-5" data-testid="portal-home">
            {/* Welcome banner */}
            <div className="fm-card p-6 bg-slate-900 text-white relative overflow-hidden">
                <div className="relative z-10 flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold mb-2">FindMed Partner Portal</div>
                        <h2 className="font-display font-black text-3xl tracking-tight mb-1">¿Necesitas una ambulancia ahora?</h2>
                        <p className="text-slate-300 text-sm">Solicítala en menos de 30 segundos. Cabina recibirá tu petición en tiempo real.</p>
                    </div>
                    <Link to="/portal/request" className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-sm font-bold uppercase tracking-wider text-sm flex items-center gap-2" data-testid="portal-request-cta">
                        <PlusCircle className="w-4 h-4" /> Solicitar Ahora
                    </Link>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi icon={Activity} label="Servicios activos" value={data.services_active} tone="accent" testId="portal-kpi-active" />
                <Kpi icon={Clock} label="Pendientes" value={data.services_pending} tone="warning" />
                <Kpi icon={TrendingUp} label="Servicios este mes" value={data.services_month} />
                <Kpi icon={Receipt} label="Saldo por pagar" value={money(data.balance)} tone={data.balance > 0 ? "accent" : "positive"} testId="portal-kpi-balance" />
            </div>

            {/* Manual del cliente corporativo */}
            <DownloadManualButton />

            {/* SLA executive widget — herramienta de cierre de venta */}
            {sla && sla.total_services > 0 && (
                <div className="fm-card p-5 border-l-4 border-l-emerald-500" data-testid="portal-sla-widget">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                        <div>
                            <h3 className="font-display font-bold text-base flex items-center gap-2">
                                <Timer className="w-4 h-4 text-emerald-600" /> Tu SLA con FindMed
                            </h3>
                            <div className="text-xs text-slate-500 font-mono">Últimos 30 días · {sla.total_services} servicios</div>
                        </div>
                        <Link to="/portal/sla" className="text-[11px] uppercase tracking-wider font-bold text-slate-600 hover:text-red-600 flex items-center gap-1">
                            Ver detalle <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Cumplimiento</div>
                            <div className={`font-mono font-black text-3xl ${sla.compliance_pct === null ? "text-slate-400" : sla.compliance_pct >= 90 ? "text-emerald-600" : sla.compliance_pct >= 75 ? "text-amber-600" : "text-red-600"}`}>
                                {sla.compliance_pct !== null ? `${Math.round(sla.compliance_pct)}%` : "—"}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Tiempo de respuesta</div>
                            <div className="font-mono font-black text-3xl">
                                {sla.avg_response_seconds ? `${Math.round(sla.avg_response_seconds / 60)} min` : "—"}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 inline-flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Dentro de SLA
                            </div>
                            <div className="font-mono font-black text-3xl text-emerald-600">{sla.within_sla}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 inline-flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3 text-red-600" /> Fuera de SLA
                            </div>
                            <div className="font-mono font-black text-3xl text-red-600">{sla.breached_sla}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Last services */}
            <div className="fm-card p-5">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display font-bold text-base">Últimos servicios</h3>
                    <Link to="/portal/services" className="text-[11px] uppercase tracking-wider font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1">
                        Ver todos <ArrowRight className="w-3 h-3" />
                    </Link>
                </div>
                {data.last_services?.length === 0 ? (
                    <div className="text-center py-10 text-sm text-slate-500">
                        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-slate-300" strokeWidth={1.5} />
                        Aún no has solicitado servicios.{" "}
                        <Link to="/portal/request" className="text-red-600 font-bold underline">Solicitar el primero</Link>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200">
                                    {["Folio", "Paciente", "Tipo", "Urgencia", "Total", "Estatus", "Fecha"].map((h) => (
                                        <th key={h} className="text-left py-2 px-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.last_services.map((s) => (
                                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="py-2 px-2">
                                            <Link to={`/portal/services/${s.id}`} className="font-mono text-xs font-bold text-slate-900 hover:text-red-600" data-testid={`portal-service-link-${s.folio}`}>{s.folio}</Link>
                                        </td>
                                        <td className="py-2 px-2">{s.patient_name}</td>
                                        <td className="py-2 px-2 text-xs">{s.service_type_name}</td>
                                        <td className="py-2 px-2"><UrgencyBadge urgency={s.urgency_level} /></td>
                                        <td className="py-2 px-2 text-right font-mono font-bold tabular-nums">{money(s.total)}</td>
                                        <td className="py-2 px-2"><StatusBadge status={s.status} /></td>
                                        <td className="py-2 px-2 text-[11px] font-mono text-slate-500">{dateFmt(s.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
